import { FlexColumn, LabeledInput, useLoading, useMoment } from '@gorila-shared-ui/components';
import { useEffect, useMemo, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { REQUIRED_FIELD_TEXT } from '../../constants/app';
import { useFeedback } from '../../hooks/useFeedback';
import { useForms } from '../../hooks/useForms';
import { getWorkersList } from '../../services/jobsService';
import { jobScheduleState, jobScheduleTouchedState, jobScheduleValidState } from '../../storage/jobStates';
import { Job, JobScheduleRequest, JobScheduleRequestTouched, Worker } from '../../types/jobs';
import EnhancedLabeledSelect from '../ui/EnhancedLabeledSelect';

type Props = {
  job: Job;
};
export function JobDetailForm({ job }: Readonly<Props>) {
  const { showFailFeedback } = useFeedback();
  const [form, setForm] = useRecoilState(jobScheduleState);
  const [jobWorkers, setJobWorkers] = useState<Worker[]>();
  const [formTouched, setFormTouched] = useRecoilState(jobScheduleTouchedState);
  const formValid = useRecoilValue(jobScheduleValidState);
  const { loading, startLoading, stopLoading } = useLoading();
  const { onTouched } = useForms<JobScheduleRequest, JobScheduleRequestTouched>(setForm, setFormTouched);
  const { formatDateFromTs } = useMoment();

  useEffect(() => {
    setForm(() => ({
      userId: job.user._id,
      scheduleDate: formatDateFromTs(job.scheduleDate, 'YYYY-MM-DDTHH:mm'),
    }));
  }, [job]);

  const onJobChange = (value: string | number, field: keyof JobScheduleRequest) => {
    setForm((prev) => {
      return {
        ...prev!,
        [field]: value || '',
      };
    });
  };

  const getWorkers = async () => {
    if (!job) return;
    startLoading();
    const { workers, error } = await getWorkersList(job.tenant._id, job.scheduleDate, job.jobType._id);
    if (!error) {
      setJobWorkers(workers);
    } else {
      showFailFeedback(error);
    }
    stopLoading();
  };

  const workersOptions = useMemo(() => {
    if (!jobWorkers) return;
    return [
      ...jobWorkers.map((worker) => ({
        id: worker.user_id,
        label: worker.full_name,
      })),
    ];
  }, [jobWorkers]);

  useEffect(() => {
    if (!job?.scheduleDate) return;
    getWorkers();
  }, [form.scheduleDate]);

  return (
    <FlexColumn>
      <LabeledInput
        label="Día de la cita"
        value={form?.scheduleDate}
        type="datetime-local"
        onChange={(value) => {
          onJobChange(value, 'scheduleDate');
        }}
        onBlur={() => onTouched('scheduleDateTouched')}
        error={formTouched.scheduleDateTouched && !formValid.scheduleDate && REQUIRED_FIELD_TEXT}
        required
      />
      <EnhancedLabeledSelect
        label="Tecnico"
        options={workersOptions}
        value={[{ id: form?.userId }]}
        onChange={(params) => onJobChange(params.option?.id as string, 'userId')}
        disabled={!form?.scheduleDate}
        onBlur={() => onTouched('userTouched')}
        error={formTouched.userTouched && !formValid.userId && REQUIRED_FIELD_TEXT}
        clearable
        searchable
        required
        fetchDataLoading={loading}
      />
    </FlexColumn>
  );
}
